import * as Sentry from '@sentry/react';
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { To, resolvePath, useOutletContext, useParams } from 'react-router-dom';
import { CTAButton } from '../../components/buttons';
import CTALink from '../../components/buttons/CTALink';
import { analytics, UNEXPECTED_ERROR } from '../../helpers/analytics';
import PageNotFoundIcon from '../../icons/error-grey.svg';
import { ResultPageLayout } from '../../layouts/ResultPageLayout';
import { RestaurantGuardOutletContext } from '../../routes/RestaurantGuard';
import { useTranslation } from 'react-i18next';

export interface ErrorBoundaryProps extends UnexpectedErrorPageProps {
	error: Error;
	componentStack: string;
	eventId: string;
	resetError(): void;
}

type ErrorBoundary = (props: ErrorBoundaryProps) => React.ReactElement;


export const ErrorBoundary: ErrorBoundary = ({ eventId, resetError, ...props }) => {
	 const firstUpdate = useRef(true);

	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		  }
		  else {
			resetError();
		  }
	}, [location.pathname]);

	try {
		return (
			<UnexpectedErrorPage onClick={() => resetError()} sentryErrorID={eventId} {...props}>
				<CTAButton
					buttonText='Report a Bug'
					variant='dark'
					onClick={() => {
						Sentry.showReportDialog({ eventId });
					}}
				/>
			</UnexpectedErrorPage>
		);
	}
	catch (e) {
		console.error(e);
		return null;
	}
};

export interface UnexpectedErrorPageProps {
	to?: To;
	text?: string;
	onClick?: () => void;
	children?: ReactNode;
	sentryErrorID?: string;
}

export function UnexpectedErrorPage({ to, text, children, onClick, sentryErrorID }: UnexpectedErrorPageProps) {
	const { t } = useTranslation();
	// we can't deconstruct here ({ restaurant }) because the restaurant itself my be not found
	// which would make the context undefined

	const context = useOutletContext<RestaurantGuardOutletContext>();
	const { restaurantSlug } = useParams();

	useEffect(() => {
		analytics.track(UNEXPECTED_ERROR, {
			...JSON.parse(JSON.stringify(window.location)),
			sentryErrorID: sentryErrorID
		});
	}, []);

	let button: ReactElement | null = null;

	if (to) {
		const path = resolvePath(to, location.pathname);

		button = <CTALink onClick={onClick} text={text || t('actions.go_back')} to={path} />;
	}
	else if (context?.restaurant) {
		button = <CTALink onClick={onClick} text={text || t('actions.go_to_homepage')} to={`/${context?.restaurant.slug}`} />;
	}
	else if (restaurantSlug) {
		button = <CTALink onClick={onClick} text={text || t('actions.go_to_homepage')} to={`/${restaurantSlug}`} />;
	}

	return (
		<ResultPageLayout headerIcon={PageNotFoundIcon} iconAltText='Broken Link Icon'>
			<ResultPageLayout.Title>{t('titles.oops')}</ResultPageLayout.Title>

			<ResultPageLayout.Body>
				<p>{t('error_pages.unexpected_body')}</p>
			</ResultPageLayout.Body>

			<ResultPageLayout.Footer>
				{button}
				{children}
			</ResultPageLayout.Footer>
		</ResultPageLayout>
	);
}
